import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, ArrowLeft, CheckCircle, CreditCard, Building, Smartphone, Gift, Clock, Shield, Loader2, XCircle } from 'lucide-react';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnrollPayment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('instamojo'); // Default to Instamojo
  const [transactionId, setTransactionId] = useState('');
  const [step, setStep] = useState('select'); // select, pay, manual, success, failed
  const [parentInfo, setParentInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('enroll_token');
    if (!token) {
      navigate('/enroll/register');
      return;
    }

    const pkg = localStorage.getItem('selected_package');
    if (pkg) {
      setSelectedPackage(JSON.parse(pkg));
    }

    loadChildren();
    loadParentInfo();
    
    // Check for payment return from Instamojo
    const paymentRequestId = searchParams.get('payment_request_id');
    const paymentId = searchParams.get('payment_id');
    if (paymentRequestId) {
      checkPaymentStatus(paymentRequestId);
    }
  }, [navigate, searchParams]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('enroll_token')}` }
  });

  const loadChildren = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/enroll/children`,
        getAuthHeaders()
      );
      setChildren(response.data.children);
      if (response.data.children.length > 0) {
        setSelectedChild(response.data.children[0]);
      }
    } catch (err) {
      console.error('Error loading children:', err);
    }
  };

  const loadParentInfo = async () => {
    try {
      console.log('Loading parent info with token:', localStorage.getItem('enroll_token')?.substring(0, 20) + '...');
      const response = await axios.get(
        `${BACKEND_URL}/api/enroll/parent-info`,
        getAuthHeaders()
      );
      console.log('Parent info loaded:', response.data);
      setParentInfo(response.data);
    } catch (err) {
      console.error('Error loading parent info:', err);
      // If we can't load parent info, show a more helpful error
      setError('Unable to load your profile. Please log in again.');
    }
  };

  const checkPaymentStatus = async (paymentRequestId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/status/${paymentRequestId}`
      );
      
      if (response.data.status === 'completed') {
        setStep('success');
      } else if (response.data.status === 'failed') {
        setStep('failed');
        setError('Payment was not successful. Please try again.');
      } else {
        // Still pending, show processing
        setStep('processing');
        // Poll again after 3 seconds
        setTimeout(() => checkPaymentStatus(paymentRequestId), 3000);
      }
    } catch (err) {
      setError('Failed to verify payment status');
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async () => {
    if (!selectedChild || !selectedPackage) return;
    
    setLoading(true);
    setError('');

    try {
      // For trial packages, create subscription directly
      if (selectedPackage.is_trial) {
        const country = localStorage.getItem('user_country') || 'IN';
        const response = await axios.post(
          `${BACKEND_URL}/api/enroll/subscription`,
          null,
          {
            params: {
              package_id: selectedPackage.id,
              child_id: selectedChild.id,
              country: country
            },
            ...getAuthHeaders()
          }
        );
        setSubscription(response.data.subscription);
        setStep('success');
        return;
      }

      // For paid packages, use Instamojo
      setStep('pay');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const initiateInstamojoPayment = async () => {
    if (!selectedPackage) {
      setError('Please select a package first');
      return;
    }
    
    if (!parentInfo) {
      setError('Loading your information... Please wait and try again.');
      loadParentInfo(); // Try to reload
      return;
    }

    // More detailed error for missing parent info
    if (!parentInfo?.email && !parentInfo?.parent_email) {
      setError('Parent email not found. Please log in again.');
      return;
    }
    if (!parentInfo?.name && !parentInfo?.parent_name) {
      setError('Parent name not found. Please update your profile.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        plan_id: selectedPackage.id, // Use package ID directly
        buyer_email: parentInfo.email || parentInfo.parent_email,
        buyer_name: parentInfo.name || parentInfo.parent_name,
        buyer_phone: parentInfo.phone || parentInfo.parent_phone || '9999999999',
        child_id: selectedChild?.id
      };
      
      console.log('Creating subscription with:', payload);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/create-subscription`,
        payload
      );

      if (response.data.success && response.data.payment_url) {
        // Store subscription info
        localStorage.setItem('pending_subscription_id', response.data.subscription_id);
        // Redirect to Instamojo payment page
        window.location.href = response.data.payment_url;
      } else {
        setError('Failed to initiate payment. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Failed to create payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitManualPayment = async () => {
    if (!subscription || !paymentMethod) return;

    setLoading(true);
    setError('');

    try {
      await axios.post(
        `${BACKEND_URL}/api/enroll/payment/manual`,
        null,
        {
          params: {
            subscription_id: subscription.id,
            payment_method: paymentMethod,
            transaction_id: transactionId
          },
          ...getAuthHeaders()
        }
      );

      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setLoading(false);
    }
  };

  const paymentMethods = [
    { id: 'instamojo', name: 'Pay Online', icon: <CreditCard className="w-5 h-5" />, description: 'Credit/Debit Card, UPI, Net Banking, Wallets' },
    { id: 'upi', name: 'Manual UPI', icon: <Smartphone className="w-5 h-5" />, description: 'Pay via QR Code' },
    { id: 'bank_transfer', name: 'Bank Transfer', icon: <Building className="w-5 h-5" />, description: 'NEFT/IMPS/RTGS' }
  ];

  const renderSelectStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Enrollment</h1>
        <p className="text-gray-600">Review and confirm your selection</p>
      </div>

      {/* Package Summary */}
      {selectedPackage && (
        <div className={`border-2 rounded-xl p-6 ${
          selectedPackage.is_trial ? 'border-green-500 bg-green-50' : 'border-orange-500 bg-orange-50'
        }`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {selectedPackage.is_trial && <Gift className="w-5 h-5 text-green-600" />}
                <h3 className="text-xl font-bold text-gray-900">{selectedPackage.name}</h3>
              </div>
              <p className="text-gray-600 text-sm">{selectedPackage.description}</p>
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                {selectedPackage.sessions_included} sessions
                {selectedPackage.is_trial ? ' (7 days)' : '/month'}
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-gray-900">{selectedPackage.price_display}</p>
              {!selectedPackage.is_trial && <p className="text-sm text-gray-500">/month</p>}
            </div>
          </div>
        </div>
      )}

      {/* Child Selection */}
      {children.length > 0 && (
        <div>
          <Label className="text-base font-semibold">Select Child</Label>
          <div className="mt-2 space-y-2">
            {children.map(child => (
              <div
                key={child.id}
                onClick={() => setSelectedChild(child)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedChild?.id === child.id
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-gray-900">{child.name}</p>
                    <p className="text-sm text-gray-500">{child.grade} • {child.school}</p>
                  </div>
                  {selectedChild?.id === child.id && (
                    <CheckCircle className="w-5 h-5 text-orange-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="pt-4 flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/enroll/child')}
          className="px-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <Button
          onClick={createSubscription}
          disabled={!selectedChild || loading}
          className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6"
        >
          {loading ? 'Processing...' : selectedPackage?.is_trial ? 'Start Free Trial' : 'Proceed to Payment'}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderPayStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Choose Payment Method</h1>
        <p className="text-gray-600">
          Amount: <span className="font-bold text-orange-600">₹{selectedPackage?.price || 'N/A'}</span>
        </p>
      </div>

      {/* Payment Methods */}
      <div className="space-y-3">
        {paymentMethods.map(method => (
          <div
            key={method.id}
            onClick={() => !method.disabled && setPaymentMethod(method.id)}
            className={`p-4 border-2 rounded-lg transition-all ${
              method.disabled
                ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                : paymentMethod === method.id
                ? 'border-orange-500 bg-orange-50 cursor-pointer'
                : 'border-gray-200 hover:border-orange-300 cursor-pointer'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                paymentMethod === method.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                {method.icon}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{method.name}</p>
                <p className="text-sm text-gray-500">{method.description}</p>
              </div>
              {paymentMethod === method.id && !method.disabled && (
                <CheckCircle className="w-5 h-5 text-orange-500" />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Instamojo - Pay Online */}
      {paymentMethod === 'instamojo' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <Shield className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h3 className="font-semibold text-green-900 mb-2">Secure Online Payment</h3>
          <p className="text-sm text-green-700 mb-4">
            Pay securely using Credit/Debit Card, UPI, Net Banking, or Wallets via Instamojo
          </p>
          <Button
            onClick={initiateInstamojoPayment}
            disabled={loading}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-3"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Pay ₹{selectedPackage?.price} Securely
              </>
            )}
          </Button>
        </div>
      )}

      {/* Manual UPI */}
      {paymentMethod === 'upi' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-3">Manual UPI Payment</h3>
          <div className="text-center">
            <div className="bg-white rounded-xl p-4 inline-block mb-4 shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_open-project-21/artifacts/v9j0pego_WhatsApp%20Image%202026-01-08%20at%2014.35.39.jpeg"
                alt="UPI QR Code"
                className="w-64 h-64 object-contain mx-auto"
              />
            </div>
            
            <div className="text-sm text-blue-800 space-y-2">
              <p className="text-lg font-semibold text-gray-900">Scan to Pay</p>
              <p><strong>UPI ID:</strong> sudarshansdaga@oksbi</p>
              <p className="text-xl font-bold text-orange-600">Amount: ₹{selectedPackage?.price}</p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                <p className="text-yellow-800 text-sm">
                  <strong>Steps:</strong><br/>
                  1. Scan QR code with any UPI app (GPay, PhonePe, Paytm)<br/>
                  2. Complete the payment<br/>
                  3. Enter your Transaction ID below
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <Label>Transaction ID / Reference Number</Label>
            <Input
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              placeholder="Enter transaction ID after payment"
              className="mt-1"
            />
          </div>
        </div>
      )}

      {/* Bank Transfer */}
      {paymentMethod === 'bank_transfer' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-3">Bank Transfer Details</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Bank:</strong> SBI</p>
            <p><strong>Account Name:</strong> Sudarshan Daga</p>
            <p><strong>Account No:</strong> Contact support for details</p>
            <p><strong>IFSC:</strong> Contact support for details</p>
            <p><strong>Amount:</strong> ₹{selectedPackage?.price}</p>
            <p className="text-blue-600 mt-2">After transfer, enter your UTR/Reference number below.</p>
          </div>

          <div className="mt-4">
            <Label>UTR / Reference Number</Label>
            <Input
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              placeholder="Enter UTR after transfer"
              className="mt-1"
            />
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="pt-4 flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => setStep('select')}
          className="px-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        {(paymentMethod === 'upi' || paymentMethod === 'bank_transfer') && (
          <Button
            onClick={submitManualPayment}
            disabled={!transactionId || loading}
            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6"
          >
            {loading ? 'Verifying...' : 'Submit Payment Details'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );

  const renderFailedStep = () => (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <XCircle className="w-10 h-10 text-red-600" />
      </div>
      
      <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Failed</h1>
      
      <p className="text-lg text-gray-600 mb-8">
        Your payment could not be processed. Please try again.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 mb-6">
          {error}
        </div>
      )}

      <Button
        onClick={() => setStep('pay')}
        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg"
      >
        Try Again
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
      
      <h1 className="text-3xl font-bold text-gray-900 mb-4">Verifying Payment</h1>
      
      <p className="text-lg text-gray-600 mb-8">
        Please wait while we verify your payment...
      </p>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      
      <h1 className="text-3xl font-bold text-gray-900 mb-4">
        {selectedPackage?.is_trial ? 'Trial Started!' : 'Enrollment Complete!'}
      </h1>
      
      <p className="text-lg text-gray-600 mb-8">
        {selectedPackage?.is_trial
          ? `Your 7-day free trial has begun. We'll contact you shortly to schedule ${selectedChild?.name}'s first session.`
          : 'Your payment has been submitted for verification. We\'ll notify you once confirmed and schedule your child\'s first session.'
        }
      </p>

      <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8 text-left">
        <h3 className="font-semibold text-green-900 mb-3">What&apos;s Next?</h3>
        <ul className="space-y-2 text-sm text-green-800">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
            Our team will review your enrollment within 24 hours
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
            We&apos;ll assign a dedicated observer to your child
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
            You&apos;ll receive a call to confirm the session schedule
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
            First session begins - we call your child at the scheduled time!
          </li>
        </ul>
      </div>

      <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-8">
        <Shield className="w-4 h-4" />
        <span>Your data is secure and protected</span>
      </div>

      <Button
        onClick={() => {
          localStorage.setItem('parent_token', localStorage.getItem('enroll_token'));
          navigate('/parent/dashboard');
        }}
        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg"
      >
        Go to Parent Dashboard
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Navigation />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-xl mx-auto">
          {/* Progress Indicator - show only for select and pay steps */}
          {step !== 'success' && (
            <div className="flex items-center justify-center gap-2 mb-8">
              {['Package', 'Account', 'Consent', 'Child', 'Payment'].map((label, idx) => (
                <React.Fragment key={label}>
                  <div className="flex items-center gap-1">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                      idx < 4 ? 'bg-green-500 text-white' : 'bg-orange-500 text-white font-bold'
                    }`}>
                      {idx < 4 ? <CheckCircle className="w-4 h-4" /> : '5'}
                    </div>
                    <span className={`text-xs hidden sm:inline ${
                      idx < 4 ? 'text-green-600' : 'text-orange-600 font-medium'
                    }`}>{label}</span>
                  </div>
                  {idx < 4 && <div className={`w-4 h-0.5 ${idx < 4 ? 'bg-green-500' : 'bg-gray-300'}`}></div>}
                </React.Fragment>
              ))}
            </div>
          )}

          <Card className="border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8">
              {step === 'select' && renderSelectStep()}
              {step === 'pay' && renderPayStep()}
              {step === 'processing' && renderProcessingStep()}
              {step === 'failed' && renderFailedStep()}
              {step === 'success' && renderSuccessStep()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EnrollPayment;
